import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Building, DollarSign, FileText, Scale, Tag } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { FormActions, FormField, FormPageHeader, FormSection } from '@/components/common/FormSection'
import { caseService } from '@/services/case-service'
import {
  Case,
  UpdateCaseDto,
  CaseStatus,
  CasePriority,
  CaseArea,
  CaseStatusLabels,
  CasePriorityLabels,
  CaseAreaLabels
} from '@/types/case'
import { useDateTimeFormat } from '@/hooks/useDateTimeFormat'
import { NativeSelect } from '@/components/ui/native-select'

export function EditCasePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { prepareDateForInput, prepareDateForSubmit } = useDateTimeFormat()
  const [caseData, setCaseData] = useState<Case | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (id) {
      loadCase(id)
    }
  }, [id])

  const loadCase = async (caseId: string) => {
    try {
      setLoading(true)
      setError(null)
      const loadedCase = await caseService.getCaseById(caseId)
      setCaseData(loadedCase)
    } catch (err) {
      setError('Erro ao carregar dados do caso')
      console.error('Error loading case:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!caseData || !id) return

    const formData = new FormData(event.currentTarget)

    const updateData: UpdateCaseDto = {
      title: formData.get('title') as string,
      description: formData.get('description') as string || undefined,
      area: formData.get('area') as CaseArea,
      status: formData.get('status') as CaseStatus,
      priority: formData.get('priority') as CasePriority,
      courtName: formData.get('courtName') as string || undefined,
      courtNumber: formData.get('courtNumber') as string || undefined,
      judgeName: formData.get('judgeName') as string || undefined,
      estimatedValue: formData.get('estimatedValue') ? parseFloat(formData.get('estimatedValue') as string) : undefined,
      successFeePercentage: formData.get('successFeePercentage') ? parseFloat(formData.get('successFeePercentage') as string) : undefined,
      responsibleLawyerName: formData.get('responsibleLawyerName') as string || undefined,
      expectedEndDate: prepareDateForSubmit(formData.get('expectedEndDate') as string) || undefined,
      tags: formData.get('tags') ? (formData.get('tags') as string).split(',').map(t => t.trim()).filter(Boolean) : undefined
    }

    try {
      setSaving(true)
      setError(null)
      await caseService.updateCase(id, updateData)
      navigate(`/cases/${id}`)
    } catch (err) {
      setError('Erro ao atualizar caso')
      console.error('Error updating case:', err)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6" aria-busy="true">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 max-w-4xl" />
      </div>
    )
  }

  if (error && !caseData) {
    return (
      <div className="p-4 sm:p-6">
        <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{error}</p>
      </div>
    )
  }

  if (!caseData) {
    return (
      <div className="p-4 py-12 text-center sm:p-6">
        <p className="text-muted-foreground">Caso não encontrado</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <FormPageHeader
        title="Editar Caso"
        description={`#${caseData.caseNumber}`}
        icon={Scale}
        backTo={`/cases/${id}`}
        backLabel="Voltar para o caso"
      />

      {error && (
        <p role="alert" className="max-w-4xl rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{error}</p>
      )}

      <form onSubmit={handleSubmit} className="flex max-w-4xl flex-col gap-4">
        <FormSection title="Dados Básicos" icon={FileText} className="sm:grid-cols-2">
          <FormField label="Título do Caso *" htmlFor="title" className="sm:col-span-2">
            <Input type="text" id="title" name="title" required defaultValue={caseData.title} />
          </FormField>
          <FormField label="Área Jurídica *" htmlFor="area">
            <NativeSelect id="area" name="area" required defaultValue={caseData.area}>
              {Object.entries(CaseAreaLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </NativeSelect>
          </FormField>
          <FormField label="Status *" htmlFor="status">
            <NativeSelect id="status" name="status" required defaultValue={caseData.status}>
              {Object.entries(CaseStatusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </NativeSelect>
          </FormField>
          <FormField label="Prioridade *" htmlFor="priority">
            <NativeSelect id="priority" name="priority" required defaultValue={caseData.priority}>
              {Object.entries(CasePriorityLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </NativeSelect>
          </FormField>
          <FormField label="Data Prevista de Conclusão" htmlFor="expectedEndDate">
            <Input type="date" id="expectedEndDate" name="expectedEndDate" defaultValue={prepareDateForInput(caseData.expectedEndDate)} />
          </FormField>
          <FormField label="Advogado Responsável" htmlFor="responsibleLawyerName" className="sm:col-span-2">
            <Input
              type="text"
              id="responsibleLawyerName"
              name="responsibleLawyerName"
              defaultValue={caseData.responsibleLawyerName || ''}
              placeholder="Nome do advogado responsável"
            />
          </FormField>
        </FormSection>

        <FormSection title="Informações do Tribunal" icon={Building} className="sm:grid-cols-2">
          <FormField label="Nome do Tribunal" htmlFor="courtName">
            <Input type="text" id="courtName" name="courtName" defaultValue={caseData.courtName || ''} />
          </FormField>
          <FormField label="Número do Processo" htmlFor="courtNumber">
            <Input type="text" id="courtNumber" name="courtNumber" defaultValue={caseData.courtNumber || ''} />
          </FormField>
          <FormField label="Nome do Juiz" htmlFor="judgeName" className="sm:col-span-2">
            <Input type="text" id="judgeName" name="judgeName" defaultValue={caseData.judgeName || ''} />
          </FormField>
        </FormSection>

        <FormSection title="Informações Financeiras" icon={DollarSign} className="sm:grid-cols-2">
          <FormField label="Valor Estimado (R$)" htmlFor="estimatedValue">
            <Input type="number" step="0.01" id="estimatedValue" name="estimatedValue" defaultValue={caseData.estimatedValue || ''} />
          </FormField>
          <FormField label="Taxa de Sucesso (%)" htmlFor="successFeePercentage">
            <Input
              type="number"
              step="0.01"
              min="0"
              max="100"
              id="successFeePercentage"
              name="successFeePercentage"
              defaultValue={caseData.successFeePercentage || ''}
            />
          </FormField>
        </FormSection>

        <FormSection title="Outras informações" icon={Tag}>
          <FormField label="Tags (separadas por vírgula)" htmlFor="tags">
            <Input type="text" id="tags" name="tags" defaultValue={caseData.tags?.join(', ') || ''} />
          </FormField>
          <FormField label="Descrição" htmlFor="description">
            <Textarea id="description" name="description" rows={4} defaultValue={caseData.description || ''} />
          </FormField>
        </FormSection>

        <FormActions>
          <Button type="button" variant="outline" onClick={() => navigate(`/cases/${id}`)}>Cancelar</Button>
          <Button type="submit" disabled={saving} loading={saving}>
            {saving ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </FormActions>
      </form>
    </div>
  )
}
